import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { authApi, usersApi } from '../services/api'

interface User {
    id: number
    username: string
    display_name: string
    email?: string
    department?: string
    role: string
    user_type?: 'regular' | 'intern'
    job_role?: string | null
    can_view_infra?: boolean
}

interface PracticasInfo {
    total_horas: number | null
    horario: string | null
    tipo_practicas: string | null
    is_configured: boolean
}

interface AuthContextType {
    user: User | null
    isLoading: boolean
    isAuthenticated: boolean
    isIntern: boolean
    practicasInfo: PracticasInfo | null
    needsPracticasSetup: boolean
    needsProfileSetup: boolean
    login: (username: string, password: string) => Promise<boolean>
    logout: () => void
    refreshUser: () => Promise<void>
    refreshPracticasInfo: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [practicasInfo, setPracticasInfo] = useState<PracticasInfo | null>(null)

    useEffect(() => {
        checkAuth()
    }, [])

    const checkAuth = async () => {
        try {
            const response = await authApi.me()
            setUser(response.data)

            // If user is an intern, fetch prácticas info
            if (response.data?.user_type === 'intern') {
                await fetchPracticasInfo(response.data.id)
            }
        } catch {
            setUser(null)
            setPracticasInfo(null)
        } finally {
            setIsLoading(false)
        }
    }

    const fetchPracticasInfo = async (userId: number) => {
        try {
            const response = await usersApi.getPracticasInfo(userId)
            setPracticasInfo(response.data)
        } catch {
            setPracticasInfo(null)
        }
    }

    const refreshPracticasInfo = async () => {
        if (user?.user_type === 'intern') {
            await fetchPracticasInfo(user.id)
        }
    }

    const login = async (username: string, password: string): Promise<boolean> => {
        try {
            const response = await authApi.login(username, password)
            if (response.data.token) {
                localStorage.setItem('auth_token', response.data.token)
            }
            await checkAuth()
            return true
        } catch {
            return false
        }
    }

    const logout = async () => {
        try {
            await authApi.logout()
        } catch {
            // Ignore logout errors
        }
        localStorage.removeItem('auth_token')
        setUser(null)
        setPracticasInfo(null)
    }

    const isIntern = user?.user_type === 'intern'
    const needsPracticasSetup = isIntern && practicasInfo !== null && !practicasInfo.is_configured
    const isAdmin = user?.role?.toLowerCase() === 'admin'
    const needsProfileSetup = isAdmin && !user?.job_role

    return (
        <AuthContext.Provider
            value={{
                user,
                isLoading,
                isAuthenticated: !!user,
                isIntern,
                practicasInfo,
                needsPracticasSetup,
                needsProfileSetup,
                login,
                logout,
                refreshUser: checkAuth,
                refreshPracticasInfo,
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext)
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}
